import { writable } from "svelte/store";
import {
  type IChat,
  type IUser,
  type IAutomationSettings,
  AutomationSetting,
  type IMonitoring,
  type IUserPaymentDetails,
  type IPhase,
} from "./../typesTs";
import { currentChat, getChat, getChats, setCurrentChat } from "./chats.store";
import { authState, goTo, isProductHost } from "../utils/productHost";
const VITE_API_LINK = "https://api.qix.cloud/";
const VITE_AUTH_M_REDIRECT_URI = "https://api.qix.cloud/microsoftRedirectSign";
const VITE_AUTH_REDIRECT_URI = "https://api.qix.cloud/googleRedirectSign";
//const VITE_BASE_LINK = "https://qixcloud-chat.pages.dev";
const VITE_ORIGIN_LINK = "https://legalpal.app/sign";
function setAuthSessionCookie(token: string): void {
  if (typeof document === "undefined" || !token) return;
  const maxAgeSeconds = 30 * 24 * 60 * 60;
  const host = window.location.hostname;
  const isLocal = host === "localhost" || host.endsWith(".pages.dev");
  const domainAttr = isLocal ? "" : "; domain=.legalpal.app";
  document.cookie = `lp_token=${encodeURIComponent(token)}; path=/; max-age=${maxAgeSeconds}; Secure; SameSite=Lax${domainAttr}`;
}

function readAuthSessionCookie(): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|; )lp_token=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function clearAuthSessionCookie(): void {
  if (typeof document === "undefined") return;
  const host = window.location.hostname;
  const isLocal = host === "localhost" || host.endsWith(".pages.dev");
  const domainAttr = isLocal ? "" : "; domain=.legalpal.app";
  document.cookie = `lp_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domainAttr}`;
}

export const user = writable<IUser | null>(null);

let userCopy: IUser;
user.subscribe((value) => {
  userCopy = value;
  console.log("userCopy", userCopy);
});

export const settings = writable<IAutomationSettings>({
  gcalendar: {},
  outlookCalendar: {},
  merusCase: {},
  fileVine: {},
  fluentCase: {},
  notify: {},
  twilio: {},
});

export const templateResponses = writable<{ text: string; id: string }[]>([]);

/**
 * Removes auth token/code query parameters from the browser address bar
 * without causing a page reload.
 */
export const cleanAuthUrlParams = (): void => {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    let changed = false;

    let cleanPathname = url.pathname;
    if (cleanPathname.length > 1 && cleanPathname.endsWith("/")) {
      cleanPathname = cleanPathname.replace(/\/+$/, "");
      changed = true;
    }

    const authKeys = [
      "iss",
      "rtnk",
      "token",
      "code",
      "session_state",
      "state",
      "scope",
      "authuser",
      "prompt",
      "hd",
      "client_info",
      "id_token",
      "access_token",
    ];
    authKeys.forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });

    if (changed) {
      const searchStr = url.searchParams.toString();
      const cleanPath =
        cleanPathname + (searchStr ? `?${searchStr}` : "") + url.hash;
      window.history.replaceState({}, document.title, cleanPath);

      if (window.parent && window.parent !== window) {
        try {
          const parentUrl = new URL(window.parent.location.href);
          let parentChanged = false;
          let parentCleanPathname = parentUrl.pathname;
          if (parentCleanPathname.length > 1 && parentCleanPathname.endsWith("/")) {
            parentCleanPathname = parentCleanPathname.replace(/\/+$/, "");
            parentChanged = true;
          }
          authKeys.forEach((key) => {
            if (parentUrl.searchParams.has(key)) {
              parentUrl.searchParams.delete(key);
              parentChanged = true;
            }
          });
          if (parentChanged) {
            const parentSearch = parentUrl.searchParams.toString();
            const parentCleanPath =
              parentCleanPathname +
              (parentSearch ? `?${parentSearch}` : "") +
              parentUrl.hash;
            window.parent.history.replaceState(
              {},
              document.title,
              parentCleanPath
            );
          }
        } catch (e) {
          // Ignore cross-origin frame restrictions
        }
      }
    }
  } catch (err) {
    console.warn("Could not clean auth URL params:", err);
  }
};

export const auth = (): Promise<IUser | void> => {
  return new Promise<IUser | void>((resolve, reject) => {
    const urlParams: URLSearchParams = new URLSearchParams(
      window.location.search
    );
    let code: string | null = urlParams.get("token") || urlParams.get("code");
    let invitation: string;
    let chatId: string;
    let rtnk: string | null = urlParams.get("rtnk");

    if (!rtnk && !code && typeof window !== "undefined" && window.parent && window.parent !== window) {
      try {
        const parentParams = new URLSearchParams(window.parent.location.search);
        rtnk = parentParams.get("rtnk");
        code = parentParams.get("token") || parentParams.get("code");
      } catch (e) {
        // Cross-origin fallback
      }
    }

    if (rtnk) {
      localStorage.setItem("token", rtnk);
      setAuthSessionCookie(rtnk);
    } else if (!localStorage.getItem("token")) {
      const shared = readAuthSessionCookie();
      if (shared) localStorage.setItem("token", shared);
    }

    cleanAuthUrlParams();

    if (code && code.includes("qix_invitaion_")) {
      invitation = code.replace("qix_invitaion_", "");
      sessionStorage.setItem("invitation", invitation);
      code = null;
    }

    if (sessionStorage.getItem("invitation") && localStorage.getItem("token")) {
      invitation = sessionStorage.getItem("invitation") || "";
      sessionStorage.removeItem("invitation");
      code = null;
    }

    if (code && code.includes("qix_shared_chat_")) {
      chatId = code.replace("qix_shared_chat_", "");
      code = null;
    }

    if (invitation && localStorage.getItem("token")) {
      addTeamMember(invitation).finally(() => {
        goTo(isProductHost() ? `${window.location.origin}/` : VITE_ORIGIN_LINK);
      });
    } else if (code && code.length) {
      if (code.includes("microsoft_code_")) {
        code = code.replace("microsoft_code_", "");
        getTokenWithMicrosoftCode(code)
          .then((token) => {
            const tokenStr = typeof token === "string" ? token : (token as any)?.token || "";
            if (tokenStr) {
              localStorage.setItem("token", tokenStr);
              setAuthSessionCookie(tokenStr);
            }
            return getUserInfo(tokenStr);
          })
          .then((info) => {
            user.set(info);
            resolve(info);
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      } else {
        getTokenWithGoogleCode(code)
          .then((token) => {
            const tokenStr = typeof token === "string" ? token : (token as any)?.token || "";
            if (tokenStr) {
              localStorage.setItem("token", tokenStr);
              setAuthSessionCookie(tokenStr);
            }
            return getUserInfo(tokenStr);
          })
          .then((info) => {
            user.set(info);
            resolve(info);
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      }
    } else if (localStorage.getItem("token")) {
      const token = localStorage.getItem("token") || "";
      getUserInfo(token)
        .then((info: IUser) => {
          let signinCount = localStorage.getItem("signinCount")
            ? localStorage.getItem("signinCount")
            : 0;
          if (typeof signinCount === "number" && signinCount >= 5) signinCount = 0;
          signinCount = Number(signinCount) + 1;
          localStorage.setItem("signinCount", String(signinCount));
          (info as any).signinCount = signinCount;
          user.set(info);
          resolve(info);
        })
        .catch((err) => {
          console.error("Token validation error in pdf-editor TS:", err);
          localStorage.removeItem("token");
          user.set(null);
          reject(err);
        });
    } else {
      resolve();
    }
  });
};

export const logout = (): void => {
  user.set(undefined);
  localStorage.removeItem("token");
  clearAuthSessionCookie();
};

export const signInWithGoogle = (): void => {
  const rootUrl: string = "https://accounts.google.com/o/oauth2/v2/auth";
  const options = {
    redirect_uri: VITE_AUTH_REDIRECT_URI,
    client_id:
      "567227164069-n724qdj6qo5ri7u2dt922j8o6f536027.apps.googleusercontent.com",
    access_type: "offline",
    response_type: "code",
    prompt: "consent",
    scope: [
      "https://www.googleapis.com/auth/userinfo.email",
      "https://www.googleapis.com/auth/userinfo.profile",
      "https://www.googleapis.com/auth/calendar.app.created",
      "https://www.googleapis.com/auth/drive.file",
      "https://www.googleapis.com/auth/gmail.send",
    ].join(" "),
  };

  const params = new URLSearchParams(options);
  const state = authState();
  if (state) params.set("state", state);
  const url: string = `${rootUrl}?${params.toString()}`;

  goTo(url);
};

export const signInWithMicrosoft = (): void => {
  const rootUrl: string =
    "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";
  const options = {
    redirect_uri: VITE_AUTH_M_REDIRECT_URI,
    client_id: "0c8ee278-e0c4-4f30-80d5-57f89fa76376",
    response_type: "code",
  };

  const params = new URLSearchParams(options);
  params.set(
    "scope",
    "profile openid email offline_access User.Read Calendars.ReadWrite Files.ReadWrite Mail.Send Mail.Send.Shared"
  );
  const state = authState();
  if (state) params.set("state", state);
  const url: string = `${rootUrl}?${params.toString()}`;

  goTo(url);
};

const getTokenWithGoogleCode = (code: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "sign/google", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        code,
        redirectUri: VITE_AUTH_REDIRECT_URI,
      }),
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};

const getTokenWithMicrosoftCode = (code: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "sign/microsoft", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        code,
        redirect_uri: VITE_AUTH_M_REDIRECT_URI,
      }),
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};

const getUserInfo = (token: string): Promise<IUser> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "auth", {
      method: "GET",
      headers: {
        Authorization: "Bearer " + token,
      },
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};

export const addTeamMember = (id: string): Promise<void> => {
  return new Promise<void>((resolve, reject) => {
    fetch(VITE_API_LINK + "team", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
      body: JSON.stringify({
        id,
      }),
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve();
      })
      .catch(() => {
        reject();
      });
  });
};

export const getSettings = (team?: boolean): Promise<void> => {
  return new Promise((resolve, reject) => {
    Promise.all([
      fetch(
        VITE_API_LINK +
          "settings/" +
          AutomationSetting.Gcalendar +
          "?userId=" +
          userCopy.appId,
        {
          method: "GET",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      )
        .then((res) => {
          if (res.status >= 300) {
            reject();
          } else {
            return res.json();
          }
        })
        .then((res) => {
          settings.update((settings) => ({
            gcalendar: res,
            merusCase: settings.merusCase,
            fileVine: settings.fileVine,
            fluentCase: settings.fluentCase,
            notify: settings.notify,
            outlookCalendar: settings.outlookCalendar,
            twilio: settings.twilio,
          }));
          resolve();
        })
        .catch(() => {
          reject();
        }),
      fetch(
        VITE_API_LINK +
          "settings/" +
          AutomationSetting.OutlookCalendar +
          "?userId=" +
          userCopy.appId,
        {
          method: "GET",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      )
        .then((res) => {
          if (res.status >= 300) {
            reject();
          } else {
            return res.json();
          }
        })
        .then((res) => {
          settings.update((settings) => ({
            gcalendar: settings.gcalendar,
            merusCase: settings.merusCase,
            fileVine: settings.fileVine,
            fluentCase: settings.fluentCase,
            notify: settings.notify,
            outlookCalendar: res,
            twilio: settings.twilio,
          }));
          resolve();
        })
        .catch(() => {
          reject();
        }),
      fetch(
        VITE_API_LINK +
          "settings/" +
          AutomationSetting.MerusCase +
          "?userId=" +
          userCopy.appId,
        {
          method: "GET",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      )
        .then((res) => {
          if (res.status >= 300) {
            reject();
          } else {
            return res.json();
          }
        })
        .then((res) => {
          settings.update((settings) => ({
            gcalendar: settings.gcalendar,
            merusCase: res,
            fileVine: settings.fileVine,
            fluentCase: settings.fluentCase,
            notify: settings.notify,
            outlookCalendar: settings.outlookCalendar,
            twilio: settings.twilio,
          }));
          resolve();
        })
        .catch(() => {
          reject();
        }),
      fetch(
        VITE_API_LINK +
          "settings/" +
          AutomationSetting.FileVine +
          "?userId=" +
          userCopy.appId,
        {
          method: "GET",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      )
        .then((res) => {
          if (res.status >= 300) {
            reject();
          } else {
            return res.json();
          }
        })
        .then((res) => {
          settings.update((settings) => ({
            gcalendar: settings.gcalendar,
            merusCase: settings.merusCase,
            fileVine: res,
            fluentCase: settings.fluentCase,
            notify: settings.notify,
            outlookCalendar: settings.outlookCalendar,
            twilio: settings.twilio,
          }));
          resolve();
        })
        .catch(() => {
          reject();
        }),
      fetch(
        VITE_API_LINK +
          "settings/" +
          AutomationSetting.Notyfy +
          "?userId=" +
          userCopy.appId,
        {
          method: "GET",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      )
        .then((res) => {
          if (res.status >= 300) {
            reject();
          } else {
            return res.json();
          }
        })
        .then((res) => {
          settings.update((settings) => ({
            gcalendar: settings.gcalendar,
            merusCase: settings.merusCase,
            fileVine: settings.fileVine,
            fluentCase: settings.fluentCase,
            notify: res,
            outlookCalendar: settings.outlookCalendar,
            twilio: settings.twilio,
          }));
          resolve();
        })
        .catch(() => {
          reject();
        }),
      fetch(
        VITE_API_LINK +
          "settings/" +
          AutomationSetting.Twilio +
          "?userId=" +
          userCopy.id,
        {
          method: "GET",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      )
        .then((res) => {
          if (res.status >= 300) {
            reject();
          } else {
            return res.json();
          }
        })
        .then((res) => {
          settings.update((settings) => ({
            gcalendar: settings.gcalendar,
            merusCase: settings.merusCase,
            fileVine: settings.fileVine,
            fluentCase: settings.fluentCase,
            notify: settings.notify,
            outlookCalendar: settings.outlookCalendar,
            twilio: res,
          }));
          resolve();
        })
        .catch(() => {
          reject();
        }),
    ])
      .then(() => {
        resolve();
      })
      .catch((err) => {
        console.error(err);
      });
  });
};

export const setSettings = <SetingsType>(
  settingsType: AutomationSetting,
  mewSettings: SetingsType,
  team?: boolean
): Promise<void> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "settings/" + settingsType, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
      body: JSON.stringify({
        settings: mewSettings || {},
        userId:
          settingsType === AutomationSetting.Twilio
            ? userCopy.id
            : userCopy.appId,
      }),
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        getSettings().finally(() => {
          resolve();
        });
      })
      .catch(() => {
        reject();
      });
  });
};

export const syncGoogleCalendar = (): Promise<[number, number, number]> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "syncGoogleCalendar", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};

export const createGoogleCalendar = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "createGoogleCalendar", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        getSettings().finally(() => {
          resolve();
        });
      })
      .catch(() => {
        reject();
      });
  });
};

export const syncOutlookCalendar = (): Promise<[number, number, number]> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "syncOutlookCalendar", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};

export const syncMerusCase = (): Promise<[number, number, number]> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "syncMerusCase", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};

export const syncFileVineContacts = (): Promise<number> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "syncFileVineContacts", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};

export const syncFileVineEvents = (projectId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "syncFileVineEvents", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        projectId,
      }),
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve();
      })
      .catch(() => {
        reject();
      });
  });
};

export const getMonitoring = (
  type: "fileVine" | "merusCase" | "gCalendar" | "notify" | "outlookCalendar"
): Promise<IMonitoring[]> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "monitoring/" + type, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};

export const setMonitoring = (
  type: "fileVine" | "merusCase" | "gCalendar" | "notify",
  monitoring: IMonitoring,
  monitoringId?: string
): Promise<IMonitoring[]> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "monitoring/" + type, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        monitoring,
        monitoringId,
      }),
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};

export const getData = (
  type: "savedMessages" | "templateResponse" | "fileVinePhases" | "merusCasePhases"
): Promise<{ id: string; [key: string]: any }[]> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "data/" + type, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
      },
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};

export const setData = (
  type: "savedMessages" | "templateResponse"  | "fileVinePhases" | "merusCasePhases",
  data: { [key: string]: any },
  dataId?: string
): Promise<void> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "data/" + type, {
      method: "POST",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        data,
        dataId,
      }),
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};

export const getFileVinePhasesFromFileVine = (): Promise<{phase: string, id: string, type: string}[]> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "getFileVinePhases", {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
        "Content-Type": "application/json",
      }
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res || []);
      })
      .catch(() => {
        reject();
      });
  });
};

export const getPaymentDetails = (): Promise<IUserPaymentDetails> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "paymentDetails", {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      },
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res);
      })
      .catch(() => {
        reject();
      });
  });
};


export const merusCaseTypes = writable<{title: string}[]>([]);
export const getMerusCaseTypes = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    fetch("https://qix.cloud/ajax/app_new.php?userId=" + userCopy.id, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        'Authorization': 'Bearer ' + localStorage.getItem('token')
      },
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        merusCaseTypes.set(res?.status?.en || res?.status || []);
        resolve();
      })
      .catch(() => {
        reject();
      });
  });
};

export const fileVinePhases = writable<IPhase[]>([]);
export const getFileVinePhases = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    getData('fileVinePhases')
      .then((phases) => {
        fileVinePhases.set(phases[0]?.phases || []);
        resolve();
      })
      .catch((err) => {
        console.error(err);
        reject(err);
      })
  });
};

export const merusCasePhases = writable<IPhase[]>([]);
export const getMerusCasePhases = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    getData('merusCasePhases')
      .then((phases) => {
        merusCasePhases.set(phases[0]?.phases || []);
        resolve();
      })
      .catch((err) => {
        console.error(err);
        reject(err);
      })
  });
};

export const getMerusCaseTypesFromMerusCase = (): Promise<{data?: {[key: number]: string}}> => {
  return new Promise((resolve, reject) => {
    fetch(VITE_API_LINK + "getMerusCaseTypes", {
      method: "GET",
      headers: {
        Authorization: "Bearer " + localStorage.getItem("token"),
        "Content-Type": "application/json",
      }
    })
      .then((res) => {
        if (res.status >= 300) {
          reject();
        } else {
          return res.json();
        }
      })
      .then((res) => {
        resolve(res || []);
      })
      .catch(() => {
        reject();
      });
  });
};


